import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery, useMutation } from '@tanstack/react-query'
import { useAuthStore } from '@/stores'
import { subscriptionService } from '@/services/api'
import { useCurrentUser } from '@/hooks/api'
import type { PlanInterval } from './PaymentPage.static'

export function usePaymentPage() {
  const { t } = useTranslation('payment')
  const [isRedirecting, setIsRedirecting] = useState(false)
  const [selectedInterval, setSelectedInterval] = useState<PlanInterval>('semiannual')
  const setAuth = useAuthStore((state) => state.setAuth)

  const { data: currentUserData } = useCurrentUser()

  useEffect(() => {
    if (currentUserData?.user && currentUserData?.facility) {
      setAuth(currentUserData.user, currentUserData.facility)
    }
  }, [currentUserData, setAuth])

  const isSuspended = currentUserData?.facility?.status === 'SUSPENDED'

  const planQuery = useQuery({
    queryKey: ['subscription', 'plan'],
    queryFn: subscriptionService.getPlan,
  })

  const checkoutMutation = useMutation({
    mutationFn: () =>
      subscriptionService.createCheckout(
        `${window.location.origin}/payment/success`,
        `${window.location.origin}/payment`,
        selectedInterval
      ),
    onSuccess: (data) => {
      setIsRedirecting(true)
      window.location.href = data.url
    },
  })

  const billingPortalMutation = useMutation({
    mutationFn: () => subscriptionService.createBillingPortal(`${window.location.origin}/payment`),
    onSuccess: (data) => {
      setIsRedirecting(true)
      window.location.href = data.url
    },
  })

  const handleSubscribe = () => {
    if (isSuspended) {
      billingPortalMutation.mutate()
    } else {
      checkoutMutation.mutate()
    }
  }

  return {
    t,
    plans: planQuery.data?.plans ?? [],
    stripeEnabled: planQuery.data?.stripeEnabled ?? false,
    isLoading: planQuery.isLoading,
    isRedirecting: checkoutMutation.isPending || billingPortalMutation.isPending || isRedirecting,
    error: checkoutMutation.error || billingPortalMutation.error,
    isSuspended,
    selectedInterval,
    setSelectedInterval,
    handleSubscribe,
  }
}
